import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {AbstractControl, UntypedFormControl} from '@angular/forms';
import {MatAutocompleteTrigger} from '@angular/material/autocomplete';
import {MatOptionSelectionChange} from '@angular/material/core';
import {ManagedSubscriber, User} from '@nh/angular-tses';
import {ROLE_METADATA} from 'app/permissions/role-metadata';
import {DepartureRole} from 'app/permissions/types';
import {map} from 'rxjs/operators';
import {sortUsers} from '../../utils/user-sort';

@Component({
  selector: 'das-user-autocomplete',
  templateUrl: './user-autocomplete.component.html',
  styleUrls: ['./user-autocomplete.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserAutocompleteComponent extends ManagedSubscriber implements OnInit, OnDestroy, AfterViewInit {
  @Input()
  public set users(users: User[]) {
    this.sortedUsers = sortUsers(users);
  }

  @Input()
  public set initial(initial: User) {
    this.setUser(initial);
  }

  @Input() public required: boolean = false;

  @Input() public set disabled(disabled: boolean) {
    disabled ? this.formControl.disable() : this.formControl.enable();
  }

  @Input()
  public set role(role: DepartureRole | string) {
    this.roleTag = role;
    this.placeholderValue = this.placeholderValue || (ROLE_METADATA[role] ? ROLE_METADATA[role].displayName : role);
  }

  @Input() public set placeholder(placeholder: string) {
    this.placeholderValue = placeholder || this.placeholderValue;
  }

  @Input() public errorMessage: string = '';

  @Input() public matHint: string = '';

  @Input() public resetOnBlur: boolean = true;

  @Output() public selected: EventEmitter<User> = new EventEmitter();

  @ViewChild(MatAutocompleteTrigger, {static: true}) public trigger: MatAutocompleteTrigger;

  @ViewChild('userInput', {static: true}) public input: ElementRef<HTMLInputElement>;

  public displayLimit: number = 20;

  public formControl = new UntypedFormControl('', (control: AbstractControl) => {
    const angularValue: string = control.value;
    // The form control value is '' on focus, regardless of the actual value.
    // This is caused by forcePanelOpen() , which is needed to open the dropdown
    // on click.  So we fall back to the value attribute of the input DOM node.
    const domValue: string = this.input ? this.input.nativeElement.value : null;
    if (typeof angularValue === 'string') {
      const selection: string = angularValue.length ? angularValue : domValue;
      if (!this.matchesUser(selection)) {
        return {requireMatch: true};
      }
    }
    return null;
  });

  public placeholderValue: string;
  public sortedUsers: User[] = [];
  public filteredUsers: User[] = [];

  public lastEmitted: User;

  public roleTag: DepartureRole | string;

  public ngOnInit(): void {
    this.subscriptions.push(
      this.formControl.valueChanges.pipe(map((name: string) => this.filter(name))).subscribe((users: User[]) => {
        this.filteredUsers = users;
        if (this.displayLimit) {
          this.filteredUsers = this.filteredUsers.slice(0, this.displayLimit);
        }
      })
    );
  }

  /**
   * On dropdown close, if there is only one user left in the filter, assign that user.
   */
  public ngAfterViewInit(): void {
    this.subscriptions.push(
      this.trigger.panelClosingActions.subscribe((e: MatOptionSelectionChange) => {
        if (!(e && e.source)) {
          if (this.filteredUsers.length === 1) {
            const user = this.filteredUsers[0];
            this.setUser(user);
          } else {
            if (this.resetOnBlur) {
              this.setUser(this.lastEmitted);
            }
          }
          this.trigger.closePanel();
        }
      })
    );
  }

  private filter(value: string | User): User[] {
    if (typeof value === 'string') {
      if (!value.length) {
        return this.sortedUsers;
      }
      const filterValue: string = value.toLowerCase();
      return this.sortedUsers.filter((user) => user.name.toLowerCase().includes(filterValue));
    } else if (!value) {
      return this.sortedUsers;
    } else {
      return [];
    }
  }

  /**
   * Returns true iff the given value is included within any usernames in the user list.
   *
   * @param value {string} The value to check.
   */
  private matchesUser(value: string): boolean {
    if (!this.sortedUsers) {
      return false;
    } else {
      const filterValue: string = value.toLowerCase();
      return this.sortedUsers.some((user) => user.name.toLowerCase().includes(filterValue));
    }
  }

  public display(user: User): string {
    return user ? user.name : null;
  }

  public optionSelected(user: User): void {
    this.lastEmitted = user;
    this.selected.emit(user);
  }

  /**
   * Force the dropdown panel to open whenever the autocomplete input is clicked,
   * rather than only on the first click.
   */
  public forcePanelOpen(): void {
    this.trigger._onChange('');
    this.trigger.openPanel();
  }

  public setUser(user: User): void {
    this.formControl.setValue(user);
    this.optionSelected(user);
  }
}
